import { defineStore } from "pinia";
import { useToast } from "vue-toastification";
import {
  User,
  KeyboardConfig,
  KeyboardConfigShort,
  BuildJob,
  BuildJobShort,
} from "@/generated/api";
import { deepCopy } from "@/utils";
import { useApi, handleApiError } from "@/composables";
import { useKeyboardStore, useFirmwareStore } from "@/stores";

function isNotNull<T>(val: T | null): val is T {
  return val != null;
}

const toast = useToast();

const BUILD_JOB_STATUS_SCORE = {
  P: 1, // pending
  R: 2, // running
  F: 3, // failure
  C: 4, // cached
  S: 5, // success
} as const;

const buildJobShortSortScore = (job: BuildJobShort): number => {
  const status = BUILD_JOB_STATUS_SCORE[job.status] ?? 0;
  const started = job.date_started != null ? 1 : 0;
  const finished = job.date_finished != null ? 1 : 0;
  return 10 * status + started + finished;
};

export const useBackendStore = defineStore("backend", {
  state: () => {
    const api = useApi();
    return {
      user: null as User | null,
      configList: [] as KeyboardConfigShort[],
      configCache: new Map<number, KeyboardConfig>(),
      buildJobList: [] as BuildJobShort[],
      buildJobCache: new Map<number, BuildJob>(),
      buildJobsByResultsHashCache: new Map<string, BuildJobShort[]>(),
      currentConfig: null as Omit<KeyboardConfig, "config"> | null,
      api,
    };
  },

  getters: {
    userLoggedIn(state): boolean {
      return state.user?.id != null;
    },

    username(state): string | undefined {
      return state.user?.username;
    },

    userIsOwner(state): boolean {
      return (
        state.user?.id != null &&
        state.currentConfig != null &&
        state.currentConfig.owner == state.user.id
      );
    },

    configModified(state): boolean {
      if (state.currentConfig == null) return false;

      const curr = state.currentConfig;
      const orig = state.configCache.get(curr.id);
      if (orig === undefined) {
        console.error("Current config not in cache");
        return false;
      }

      const kb = useKeyboardStore();
      return (
        curr.name != orig.name ||
        curr.description != orig.description ||
        curr.public != orig.public ||
        kb.modified
      );
    },

    currentConfigFinal(state): KeyboardConfig | null {
      if (state.currentConfig == null) return null;
      const kb = useKeyboardStore();
      return { config: kb.finalConfig, ...state.currentConfig };
    },
  },

  actions: {
    fetchUser() {
      if (this.user != null) {
        return Promise.resolve(this.user);
      } else {
        return this.api
          .userCurrentRetrieve()
          .then((response) => (this.user = response.data))
          .catch(handleApiError);
      }
    },

    setCurrentConfig(id: number) {
      const setConfig = (c: KeyboardConfig) => {
        const kb = useKeyboardStore();
        const firmware = useFirmwareStore();
        // Split the API metadata from the raw firmware configuration
        const { config, ...metadata } = c;
        kb.setConfig(config);
        this.currentConfig = metadata;
        firmware.invalidate();
      };

      const config = this.configCache.get(id);
      if (config) {
        const c = deepCopy(config);
        setConfig(c);
        return Promise.resolve(c);
      } else {
        return this.fetchFullConfig(id).then((c) => {
          setConfig(c);
          return c;
        });
      }
    },

    fetchConfigList() {
      return this.api
        .configList()
        .then((configs) => {
          this.configList = configs.data;
          return this.configList;
        })
        .catch(handleApiError);
    },

    fetchFullConfig(id: number) {
      return this.api
        .configRetrieve(id)
        .catch(handleApiError)
        .then((response) => {
          const c = response.data;
          this.configCache.set(c.id, deepCopy(c));
          return c;
        });
    },

    fetchBuildJobList() {
      return this.api
        .jobList()
        .then((jobs) => {
          this.buildJobList = jobs.data.filter(isNotNull);
          return this.buildJobList;
        })
        .catch(handleApiError);
    },

    fetchBuildJobsByResultsHash(hash: string) {
      return this.api
        .jobByResultsHashList(hash)
        .then((resp) => {
          const jobs = deepCopy(resp.data);
          // sort jobs such that successful ones come first, so in most cases we can just use jobs[0]
          jobs.sort(
            (a, b) => buildJobShortSortScore(b) - buildJobShortSortScore(a)
          );
          this.buildJobsByResultsHashCache.set(hash, jobs);
          return jobs;
        })
        .catch(handleApiError);
    },

    fetchFullBuildJob(id: number) {
      return this.api
        .jobRetrieve(id)
        .then((job) => {
          this.buildJobCache.set(id, deepCopy(job.data));
          return job.data;
        })
        .catch(handleApiError);
    },

    async getFullBuildJob(id: number) {
      return this.buildJobCache.get(id) ?? (await this.fetchFullBuildJob(id));
    },

    async getBuildJobsByResultsHash(hash: string) {
      return (
        this.buildJobsByResultsHashCache.get(hash) ??
        (await this.fetchBuildJobsByResultsHash(hash))
      );
    },

    updateCurrentConfig() {
      const config = this.currentConfigFinal;
      if (config == null) {
        throw Error("No current config selected, cannot update.");
      }
      return this.api
        .configUpdate(config.id, config)
        .catch(handleApiError)
        .then((response) => {
          const c = response.data;
          this.configCache.set(c.id, deepCopy(c));
          toast.success("Configuration has been updated");
          return this.setCurrentConfig(c.id);
        })
        .then((_c) => this.fetchConfigList());
    },

    deleteCurrentConfig() {
      const config = this.currentConfigFinal;
      if (config == null) {
        throw Error("No current config selected, cannot delete.");
      }
      return this.api
        .configDestroy(config.id)
        .catch(handleApiError)
        .then((_response) => {
          toast.success("Configuration has been deleted");
          return this.fetchConfigList();
        })
        .then((_c) => this.setCurrentConfig(this.configList[0].id));
    },

    createNewConfig() {
      const config = this.currentConfigFinal;
      if (config == null) {
        throw Error("No current config selected, cannot save.");
      }
      const newConfig = {
        name: config.name,
        description: config.description,
        config: config.config,
        public: config.public,
      };
      return this.api
        .configCreate(newConfig)
        .catch(handleApiError)
        .then((response) => {
          const c = response.data;
          this.configCache.set(c.id, deepCopy(c));
          toast.success("Configuration has been saved");
          return this.setCurrentConfig(c.id);
        })
        .then((_c) => this.fetchConfigList());
    },
  },
});
